import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Flex, Text, Icon, VStack, HStack, Box } from "@chakra-ui/react";
import { FiUpload, FiFileText } from "react-icons/fi";
import { toast } from "react-toastify";
import { PrimayButton } from "components/customs/buttons";
import Papa from "papaparse";
import { API_SUCCESS_RESPONSE_TAG } from "lib/constants";
import { bulkCreateRewardsThunk } from "store/slices/rewards";
import { useDispatch } from "react-redux";
import { IoMdCloseCircle } from "react-icons/io";
import { useLoadingSelector } from "store/slices/loading";
import { format, isValid, parse } from "date-fns";

const CSV_ACCEPT_STRING = ".csv,text/csv,application/vnd.ms-excel";

const validateCsvFile = (file) => {
  const isCsv = file?.name?.toLowerCase().endsWith(".csv");

  if (isCsv) {
    return null;
  }

  return {
    code: "file-invalid-type",
    message: "Invalid file type. Only CSV files are allowed.",
  };
};

const normalizeRewardDate = (value) => {
  if (value == null || value === "") return value;

  if (typeof value === "number") {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    excelEpoch.setUTCDate(excelEpoch.getUTCDate() + value);
    return format(excelEpoch, "yyyy-MM-dd");
  }

  const nativeDate = new Date(value);
  if (isValid(nativeDate)) {
    return format(nativeDate, "yyyy-MM-dd");
  }

  const knownFormats = ["yyyy-MM-dd", "MM/dd/yyyy", "dd/MM/yyyy", "M/d/yyyy", "d/M/yyyy"];
  for (const dateFormat of knownFormats) {
    const parsedDate = parse(String(value), dateFormat, new Date());
    if (isValid(parsedDate)) {
      return format(parsedDate, "yyyy-MM-dd");
    }
  }
  return value;
};

export const FileUploadDropzone = ({
  label,
  description,
  maxFileSize,
  acceptedFiles,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const [uploadedFile, setUploadedFile] = useState(null);
  const loading = useLoadingSelector();

  // Handle dropped files
  const onDrop = useCallback(
    (acceptedDropFiles, fileRejections) => {
      // Handle valid files
      if (acceptedDropFiles.length > 1) {
        toast.error("Only one file is allowed.");
      }

      if (acceptedDropFiles.length > 0 && acceptedDropFiles.length < 2) {
        setUploadedFile(acceptedDropFiles[0]);
      }

      // Handle rejections (invalid files)
      fileRejections.forEach((rejection) => {
        const errorCodes = rejection.errors.map((error) => error.code);

        if (errorCodes.includes("file-too-large")) {
          toast.error("File is too large. Max file size is 5MB.");
          return;
        }

        if (errorCodes.includes("file-invalid-type")) {
          toast.error("Invalid file type. Only CSV files are allowed.");
          return;
        }

        toast.error("Invalid file.");
      });
    },
    [toast]
  );

  // React Dropzone hook
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFiles === ".csv" ? CSV_ACCEPT_STRING : acceptedFiles,
    validator: acceptedFiles === ".csv" ? validateCsvFile : undefined,
    maxSize: maxFileSize,
  });

  const handleCreateBulkReward = () => {
    Papa.parse(uploadedFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: "greedy",
      transform: (value, field) => {
        if(field === 'Reward Date') {
          return normalizeRewardDate(value);
        }

        return typeof value === "string" ? value.trim() : value;
      },
      complete: (results) => {
        dispatch(bulkCreateRewardsThunk(results.data)).then((res) => {
          if (res.payload === API_SUCCESS_RESPONSE_TAG) {
            handleClose();
          }
        });
      },
      error: (error) => {
        toast.error(`Error parsing CSV: ${error.message}`);
        console.error("Error parsing CSV:", error.message);
      },
    });
  };

  if (uploadedFile) {
    return (
      <Flex
        flex="1"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <VStack spacing={4}>
          <Box position="relative">
            <Icon as={FiFileText} w={12} h={12} color="navy.700" />
            <Box position="absolute" zIndex={99} top={-2} right={"-6px"}>
              <Icon
                w={6}
                h={6}
                as={IoMdCloseCircle}
                color="red.600"
                onClick={() => setUploadedFile(null)}
                cursor="pointer"
              />
            </Box>
          </Box>
          <Text>{uploadedFile.name}</Text>
          <HStack>
            <PrimayButton
              isLoading={loading}
              onClick={handleCreateBulkReward}
              width="100%"
              key="create"
              label="Create Rewards"
            />
          </HStack>
        </VStack>
      </Flex>
    );
  }

  return (
    <Flex
      flex="1"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      {...getRootProps()}
      border="2px dashed"
      // borderColor="navy.700"
      borderRadius="md"
      p={8}
      textAlign="center"
      cursor="pointer"
      color="navy.600"
      _hover={{ borderColor: "navy.700", color: "navy.700" }}
    >
      <input
        {...getInputProps({
          accept:
            acceptedFiles === ".csv"
              ? CSV_ACCEPT_STRING
              : acceptedFiles,
        })}
      />
      <VStack spacing={4}>
        <Box>
          <Icon as={FiUpload} boxSize={8} />
          <Text fontSize="lg">{label}</Text>
          <Text fontSize="xs">{`(${description})`}</Text>
        </Box>
        <Text fontSize="lg" margin={0}>
          or
        </Text>
        <PrimayButton
          width="100%"
          key="upload-button"
          label="Click to Upload"
        />
      </VStack>
    </Flex>
  );
};
